import BubbleCommon, {
  compareKeyMetricsEngagementTips,
  summaryMentionsRatioTips,
  summaryNegativeRatioTips,
  summaryPositiveRatioTips,
} from '@op/components/BubbleCommon';
import HoverSortCommon from '@op/components/HoverSortCommon';
import { buriedBtnPoint } from '@op/operationLog/buriedPoint';
import { arrayToSimpleObj } from '@op/utils';
import { decimalFormatPercentage, sentimentFormat } from '@op/utils/value';
import React, { useEffect, useState } from 'react';
import { getLocale, useIntl } from 'umi';
import PercentRatioOrChart from './components/PercentRatioOrChart';
import { useMetricsChannel } from '@op/hooks/useMetricsChannel';

export const tableColumnsConfig = [
  {
    key: 'mentions',
    header: 'Mentions',
    valueKey: 'mentions',
    hasCharts: true,
    changeSort: true,
  },
  {
    key: 'mentions_ratio',
    header: 'Mentions%',
    valueKey: 'mentions_ratio',
    formatValue: decimalFormatPercentage,
    hasCharts: true,
    sortItem: 'mentions',
    changeSort: true,
    tips: summaryMentionsRatioTips,
  },
  {
    key: 'avg_sentiment',
    header: 'Sentiment',
    valueKey: 'avg_sentiment',
    formatValue: sentimentFormat,
    changeSort: true,
    isSentiment: true,
  },
  {
    key: 'negative_mentions_ratio',
    header: 'Negative%-2',
    valueKey: 'negative_mentions_ratio',
    formatValue: decimalFormatPercentage,
    changeSort: true,
    sortItem: 'negative_mentions',
    tips: summaryNegativeRatioTips,
    tableProgress: true,
  },
  {
    key: 'positive_mentions_ratio',
    header: 'Positive%-2',
    valueKey: 'positive_mentions_ratio',
    formatValue: decimalFormatPercentage,
    sortItem: 'positive_mentions',
    changeSort: true,
    tips: summaryPositiveRatioTips,
    tableProgress: true,
  },
  {
    key: 'engagement',
    header: 'Engagement',
    valueKey: 'engagement',
    changeSort: true,
    tips: compareKeyMetricsEngagementTips,
  },
];

export const topicTrackingSortItemMap = arrayToSimpleObj(
  tableColumnsConfig,
  'sortItem',
  'valueKey',
);

export default function useTopicTrackingTableColumns({
  timelineMap,
  filter,
  transTopic,
  sortInfo,
  setPageInfo,
  topicLevel,
  gameInfo,
  isTopicSentiment = 'sentiment'
}: any) {
  const { formatMessage } = useIntl();
  const [sort, setSort] =
    sortInfo ||
    useState<{ by: string; order: 'desc' | 'asc' }>({
      by: 'avg_sentiment',
      order: 'asc',
    });
  const [columns, setColumns] = useState<Array<any>>([]);
  const [sortList, setSortList] = useState<Array<string>>([]);

  const MergeChannelIconElement = useMetricsChannel({ gameInfo });

  useEffect(() => {
    const sortList: Array<string> = [];
    const columns: Array<any> = tableColumnsConfig.map(
      ({
        key,
        header,
        valueKey,
        formatValue,
        hasCharts,
        changeSort,
        tips,
        isSentiment,
        tableProgress,
      }) => {
        let headerText: any = formatMessage({ id: header });

        if (key === 'avg_sentiment' && isTopicSentiment !== 'sentiment') {
          headerText = formatMessage({ id: 'Topic Sentiment' });
        }

        if (changeSort) {
          headerText = (
            <HoverSortCommon
              sort={sort}
              sortOptions={[
                {
                  text: headerText,
                  value: valueKey,
                },
                {
                  text: formatMessage({ id: `${header}: Change%` }),
                  value: valueKey + '_qoq',
                },
              ]}
              sortChange={(sort: { by: string; order: 'asc' | 'desc' }) => {
                if (sort.by.includes('qoq') && setPageInfo) {
                  setPageInfo({ page: 1, page_size: 1000 });
                }
                buriedBtnPoint('STO_ListRank', sort.by);
                setSort(sort);
              }}
            >
              <BubbleCommon
                tipsMap={tips}
                showIcon={false}
                placement="top"
                extraContent={
                  <MergeChannelIconElement
                    metric={key}
                  ></MergeChannelIconElement>
                }
              >
                <span>{headerText}</span>
              </BubbleCommon>
            </HoverSortCommon>
          );
        } else {
          sortList.push(valueKey);
        }

        return {
          key: key,
          header: headerText,
          align: 'right',
          width: hasCharts ? 260 : 160,
          render: (record: any) => {
            const topic = record.topic.toLocaleLowerCase();
            return (
              <PercentRatioOrChart
                filter={filter}
                data={record}
                valueKey={valueKey}
                header={formatMessage({ id: header })}
                formatValue={formatValue}
                timeline={timelineMap[valueKey]?.[topic]}
                name={transTopic(topic, topicLevel, getLocale())}
                isSentiment={isSentiment}
                tableProgress={tableProgress}
              ></PercentRatioOrChart>
            );
          },
        };
      },
    );

    setColumns(columns);
    setSortList(sortList);
  }, [timelineMap, sort, filter, formatMessage]);

  return {
    columns,
    sortList,
    sort,
    setSort,
  };
}
